import React from "react";

import { formatDate, addLocalDays } from "../../../requests/utilities/helpers";
import { formatName, formatPersonName, get_t_str, getCardColor2 } from "../../../../../helpers/helpers";

const dateOptions = { weekday: "short", month: "short", day: "numeric" };
const dowNums = [0, 1, 2, 3, 4, 5, 6];

const isNumber = (value) =>
  typeof value === "number" && !Number.isNaN(value);

const getMemberId = (shift) => {
  if (typeof shift.store_team_member === "object") {
    return shift.store_team_member?.id;
  }
  if (shift.store_team_member !== undefined) {
    return shift.store_team_member;
  }
  return shift.store_team_member_id;
};

const getShiftHours = (shift) => {
  if (isNumber(shift.hours)) {
    return shift.hours;
  }
  if (isNumber(shift.shift_length)) {
    return shift.shift_length;
  }
  if (isNumber(shift.shift_start) && isNumber(shift.shift_end)) {
    return shift.shift_end - shift.shift_start;
  }
  return 0;
};

const isOpenShift = (shift) => {
  if (shift.status === "AVAILABLE") {
    return true;
  }
  const memberId = getMemberId(shift);
  return memberId === null || memberId === undefined || memberId < 0;
};

const getAvailabilityText = (availList, timeoffList) => {
  if (timeoffList?.length > 0) {
    return "Time Off";
  }
  if (!availList || availList.length === 0) {
    return "";
  }
  if (
    availList[0]?.available_from === 0 &&
    availList[0]?.available_to === 0
  ) {
    return "Unavailable";
  }
  return "";
};

const buildDayDates = (weekStartDate) =>
  dowNums.map((dow) => {
    const date = addLocalDays(weekStartDate, dow);
    return date ? formatDate(date) : null;
  });

const buildDayNames = (weekStartDate) =>
  dowNums.map((dow) => {
    const date = addLocalDays(weekStartDate, dow);
    return date ? date.toLocaleDateString("en-us", dateOptions) : "";
  });

const getRoleColorClass = (roleId, roleNames) =>
  getCardColor2(roleId, roleNames, false, false);

export default function AutoScheduleWeeklyView(props) {
  const {
    schedule,
    storeTeamMembers,
    week_start_date,
    storeNames,
    store_id,
    roleNames,
    headerTitle,
    headerSubtitle,
    headerContent,
    changedShifts = [],
    highlightLabel = "Changed shift",
  } = props;

  if (!week_start_date) {
    return null;
  }

  const dayDates = buildDayDates(week_start_date);
  const dayNames = buildDayNames(week_start_date);
  const dayIndexLookup = dayDates.reduce((acc, date, index) => {
    if (date) {
      acc[date] = index;
    }
    return acc;
  }, {});

  const openShifts = (schedule || []).filter(isOpenShift);
  const scheduledShifts = (schedule || []).filter((shift) => !isOpenShift(shift));

  const shiftsByMember = new Map();
  scheduledShifts.forEach((shift) => {
    const memberId = getMemberId(shift);
    if (!memberId || memberId < 0) {
      return;
    }
    const dayIndex = dayIndexLookup[shift.date];
    if (dayIndex === undefined) {
      return;
    }
    if (!shiftsByMember.has(memberId)) {
      shiftsByMember.set(memberId, {});
    }
    const memberDays = shiftsByMember.get(memberId);
    if (!memberDays[dayIndex]) {
      memberDays[dayIndex] = [];
    }
    memberDays[dayIndex].push(shift);
  });

  const memberRows = (storeTeamMembers || [])
    .filter((member) => !member.isRoleHeader)
    .map((member) => {
      const memberDays = shiftsByMember.get(member.id) || {};
      const row = {
        ...member,
        employee_id: member.employee_id || member.id,
        displayName: formatName(`${member.first_name} ${member.last_name}`),
        hours: 0,
        other_store_hours: 0,
      };

      dowNums.forEach((dow) => {
        const shifts = (memberDays[dow] || []).sort(
          (a, b) => a.shift_start - b.shift_start
        );
        row[dow] = shifts;
        shifts.forEach((shift) => {
          const shiftHours = getShiftHours(shift);
          row.hours += shiftHours;
          if (shift.other_store) {
            row.other_store_hours += shiftHours;
          }
        });
      });

      return row;
    });

  const dailyOpenShifts = dowNums.map((dow) => {
    const date = dayDates[dow];
    return openShifts.filter((shift) => shift.date === date);
  });

  const totalOpenShiftHours = openShifts.reduce(
    (total, shift) => total + getShiftHours(shift),
    0
  );

  const calculateTotalHoursForDay = (dow) => {
    const scheduledHours = memberRows.reduce((total, member) => {
      if (!member[dow] || member[dow].length === 0) {
        return total;
      }
      const dayHours = member[dow].reduce(
        (sum, shift) => sum + (shift.other_store ? 0 : getShiftHours(shift)),
        0
      );
      return total + dayHours;
    }, 0);

    const openHours = dailyOpenShifts[dow].reduce(
      (total, shift) => total + getShiftHours(shift),
      0
    );

    return scheduledHours + openHours;
  };

  const totalHoursPerDay = dowNums.map((dow) =>
    calculateTotalHoursForDay(dow)
  );

  const totalMemberHours = memberRows.reduce(
    (total, member) => total + member.hours - member.other_store_hours,
    0
  );

  const totalHours = totalMemberHours + totalOpenShiftHours;

  const storeName = storeNames?.find((s) => s.id === Number(store_id))
    ?.store_name;
  const showHeader = headerTitle || headerSubtitle || headerContent || storeName;
  const formatCompactTime = (start, end) => {
    const startStr = get_t_str(start)?.replace(":00", "")?.replace("am", "a")?.replace("pm", "p") || "";
    const endStr = get_t_str(end)?.replace(":00", "")?.replace("am", "a")?.replace("pm", "p") || "";
    return `${startStr}-${endStr}`;
  };

  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-4 mb-8">
      {showHeader && (
        <div className="mb-3 flex flex-wrap items-start justify-between gap-3">
          <div className="min-w-0">
            <h3 className="text-sm font-semibold text-slate-900">
              {headerTitle || storeName}
            </h3>
            {(headerSubtitle || (headerTitle && storeName)) && (
              <div className="text-xs text-slate-500">
                {headerSubtitle || storeName}
              </div>
            )}
          </div>
          {headerContent}
        </div>
      )}
      <table className="w-full text-xs text-left text-gray-900 table-fixed">
        <colgroup>
          <col className="w-[140px]" />
          <col className="w-[50px]" />
          <col className="w-[calc((100%-190px)/7)]" />
          <col className="w-[calc((100%-190px)/7)]" />
          <col className="w-[calc((100%-190px)/7)]" />
          <col className="w-[calc((100%-190px)/7)]" />
          <col className="w-[calc((100%-190px)/7)]" />
          <col className="w-[calc((100%-190px)/7)]" />
          <col className="w-[calc((100%-190px)/7)]" />
        </colgroup>
        <thead className="text-xs text-gray-700 uppercase bg-slate-50">
          <tr>
            <th scope="col" className="py-2 px-2">
              Employee
            </th>
            <th scope="col" className="py-2 pl-1 pr-4 text-right">
              Hrs
            </th>
            {dayNames.map((dayName) => {
              const parts = dayName.split(", ");
              const dow = parts[0];
              const date = parts[1] || "";
              return (
                <th scope="col" className="py-2 px-1 text-center" key={dayName}>
                  <div className="font-semibold">{dow}</div>
                  <div className="font-normal text-slate-500">{date}</div>
                </th>
              );
            })}
          </tr>
        </thead>
        <tbody>
          {memberRows.map((member) => {
            return (
              <tr
                key={member.employee_id}
                className="border-t border-slate-200"
              >
                <td className="py-1.5 px-2">
                  <div className="flex min-w-0 items-center gap-1.5">
                    <div className="truncate font-medium text-gray-900" title={member.displayName}>
                      {formatPersonName(member)}
                    </div>
                  </div>
                </td>
                <td className="py-1.5 pl-1 pr-4 text-right font-medium">
                  {(member.hours - member.other_store_hours).toFixed(1)}
                </td>
                {dowNums.map((dow) => {
                  if (member[dow]?.length > 0) {
                    return (
                      <td key={dow} className="py-1 px-1">
                        <div className="flex flex-col gap-1.5">
                          {member[dow].map((shift, idx) => {
                            const roleId = shift.role_id || shift.role_id_id;
                            const roleColorClass = getRoleColorClass(roleId, roleNames);
                            return (
                              <div
                                key={idx}
                                className={`min-w-0 text-white text-[10px] leading-4 px-1 py-0.5 rounded text-center ${roleColorClass} ${changedShifts.includes(shift) ? "ring-2 ring-violet-500 ring-offset-1" : ""}`}
                                title={changedShifts.includes(shift) ? `${highlightLabel}: ${formatCompactTime(shift.shift_start, shift.shift_end)}` : formatCompactTime(shift.shift_start, shift.shift_end)}
                              >
                                <span className="block truncate">{formatCompactTime(shift.shift_start, shift.shift_end)}</span>
                              </div>
                            );
                          })}
                        </div>
                      </td>
                    );
                  }
                  const availText = getAvailabilityText(
                    member[`${dow}_availability`],
                    member[`${dow}_timeoff`]
                  );
                  return (
                    <td key={dow} className="py-1 px-1 text-center">
                      {availText && (
                        <span className={`text-[10px] ${availText === "Time Off" ? "text-amber-600" : "text-slate-400"}`}>
                          {availText}
                        </span>
                      )}
                    </td>
                  );
                })}
              </tr>
            );
          })}
        </tbody>
        <tfoot>
          {totalOpenShiftHours > 0 && (
            <tr className="border-t border-slate-200 bg-slate-50">
              <td className="py-1.5 px-2 font-medium">
                <div className="flex min-w-0 items-center gap-1.5">
                  <span>Open Shifts</span>
                </div>
              </td>
              <td className="py-1.5 pl-1 pr-4 text-right font-medium">
                {totalOpenShiftHours.toFixed(1)}
              </td>
              {dailyOpenShifts.map((day, index) => (
                <td key={index} className="py-1 px-1">
                  <div className="flex flex-col gap-1.5">
                    {day.map((shift, idx) => {
                      const roleId = shift.role_id || shift.role_id_id;
                      const roleColorClass = getRoleColorClass(roleId, roleNames);
                      return (
                        <div
                          key={idx}
                          className={`min-w-0 text-white text-[10px] leading-4 px-1 py-0.5 rounded text-center ${roleColorClass || "bg-slate-400"} ${changedShifts.includes(shift) ? "ring-2 ring-violet-500 ring-offset-1" : ""}`}
                          title={changedShifts.includes(shift) ? `${highlightLabel}: ${formatCompactTime(shift.shift_start, shift.shift_end)}` : formatCompactTime(shift.shift_start, shift.shift_end)}
                        >
                          <span className="block truncate">{formatCompactTime(shift.shift_start, shift.shift_end)}</span>
                        </div>
                      );
                    })}
                  </div>
                </td>
              ))}
            </tr>
          )}
          <tr className="border-t-2 border-slate-300 bg-slate-50 font-medium">
            <td className="py-2 px-2">Total</td>
            <td className="py-2 pl-1 pr-4 text-right">{totalHours.toFixed(1)}</td>
            {totalHoursPerDay.map((hours, index) => (
              <td key={index} className="py-2 px-1 text-center">
                {hours.toFixed(1)}
              </td>
            ))}
          </tr>
        </tfoot>
      </table>
    </div>
  );
}
